// Google Workspace for the company (ADR 0037, spec 9.3, story GA1): the console drawer's three
// steps. Types from @vd/api-client (crates/api-types/src/mailboxes.rs).
import { queryOptions } from "@tanstack/react-query";
import type { GoogleCheckOutcome, GoogleCheckStart, GoogleSetup } from "@vd/api-client";
import { api } from "./client";

export const googleKeys = {
  setup: ["admin", "google"] as const,
};

export const googleSetupQuery = queryOptions({
  queryKey: googleKeys.setup,
  queryFn: () => api<GoogleSetup>("/v1/admin/google"),
});

export const googleApi = {
  start: () => api<GoogleSetup>("/v1/admin/google/start", { method: "POST", body: {} }),
  check: () => api<GoogleCheckStart>("/v1/admin/google/check", { method: "POST", body: {} }),
  answer: (warning_screen: boolean) =>
    api<GoogleSetup>("/v1/admin/google/check", { method: "PUT", body: { warning_screen } }),
  domains: (domains: string[]) =>
    api<GoogleSetup>("/v1/admin/google/domains", { method: "PUT", body: { domains } }),
  turnOn: () => api<GoogleSetup>("/v1/admin/google/turn-on", { method: "POST", body: {} }),
  turnOff: () => api<GoogleSetup>("/v1/admin/google/turn-off", { method: "POST", body: { confirm: true } }),
};

/** Leaves for Google's sign-in in this tab (Google sends the admin back to the drawer). An
 * object, so tests can replace it. */
export const googleNav = {
  to: (url: string) => window.location.assign(url),
};

/** Where an admin trusts Kite's client: Security, Access and data control, API controls. */
export const GOOGLE_ADMIN_URL = "https://admin.google.com/ac/owl";

/** The toast after Google sends the admin back (`?google_check=`). */
export const CHECK_TOAST: Record<
  GoogleCheckOutcome,
  { text: string; tone: "ok" | "info" | "danger" } | null
> = {
  checked: null,
  personal: { text: "That's a personal Google account. Use your Workspace account.", tone: "danger" },
  declined: { text: "Google didn't let Kite in. Check step 1.", tone: "danger" },
  too_many_domains: { text: "Remove a domain first. Kite keeps at most 20.", tone: "danger" },
  not_admin: { text: "Only a Kite admin can check Google.", tone: "danger" },
  expired: { text: "That check expired. Try again.", tone: "danger" },
  failed: { text: "Couldn't reach Google. Try again.", tone: "danger" },
};

export const GOOGLE_CHECK_OUTCOMES = Object.keys(CHECK_TOAST) as [
  GoogleCheckOutcome,
  ...GoogleCheckOutcome[],
];

/** The card's pill and line (DESIGN.md "Console: Google Workspace"): Off, Setting up or On, and
 * the domains. Installed once the setup started. */
export function googleCard(s: GoogleSetup): {
  label: string;
  status: "connected" | "not_set_up" | "attention";
  summary: string | null;
  installed: boolean;
} {
  const summary = s.domains.length ? s.domains.join(", ") : null;
  if (s.state === "on") return { label: "On", status: "connected", summary, installed: true };
  if (s.state === "setting_up")
    return { label: "Setting up", status: "not_set_up", summary, installed: true };
  return { label: "Off", status: "not_set_up", summary: null, installed: false };
}
